import type { AxiosInstance } from 'axios';
import axios from 'axios';
import authService from './auth.service';
import type {
  ApplicableTemplate,
  AssistanceRequestBody,
  CaseSummary,
  DownloadUrlResponse,
  SignContractResponse,
  TimelineEvent,
  UploadConfirmResponse,
  UploadPresignResponse,
  UsOfferingAnswers,
  UsOfferingInfo,
} from '../types/contracts';
import type { UploadProgress } from '../lib/uploadProgress';

export interface SignedDocument {
  document_id: string;
  sequence: number;
  file_hash: string;
  size_bytes: number | null;
  mime_type: string | null;
  uploaded_at: string;
  uploaded_by: string;
  state_slug: string;
}

class ContractsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async listApplicable(): Promise<ApplicableTemplate[]> {
    const res = await this.api.get<ApplicableTemplate[]>('/api/v1/contracts/applicable');
    return res.data;
  }

  /** Whether the caller's Membership Agreement is approved (S-41). */
  async getMembership(): Promise<{ approved: boolean }> {
    const res = await this.api.get<{ approved: boolean }>('/api/v1/contracts/membership');
    return res.data;
  }

  async startCase(templateVersionId: string): Promise<{ case_id: string }> {
    const res = await this.api.post<{ case_id: string }>('/api/v1/contracts/cases', {
      template_version_id: templateVersionId,
    });
    return res.data;
  }

  async getCase(caseId: string): Promise<CaseSummary> {
    const res = await this.api.get<CaseSummary>(`/api/v1/contracts/cases/${caseId}`);
    return res.data;
  }

  async getDownloadUrl(caseId: string): Promise<DownloadUrlResponse> {
    const res = await this.api.get<DownloadUrlResponse>(
      `/api/v1/contracts/cases/${caseId}/download`,
    );
    return res.data;
  }

  async presignUpload(caseId: string): Promise<UploadPresignResponse> {
    const res = await this.api.post<UploadPresignResponse>(
      `/api/v1/contracts/cases/${caseId}/upload-presign`,
    );
    return res.data;
  }

  async confirmUpload(caseId: string, objectKey: string): Promise<UploadConfirmResponse> {
    const res = await this.api.post<UploadConfirmResponse>(
      `/api/v1/contracts/cases/${caseId}/upload-confirm`,
      { object_key: objectKey },
    );
    return res.data;
  }

  async signContract(
    caseId: string,
    signerName: string,
    usOffering?: UsOfferingAnswers,
  ): Promise<SignContractResponse> {
    const res = await this.api.post<SignContractResponse>(
      `/api/v1/contracts/cases/${caseId}/sign`,
      {
        signer_name: signerName,
        purchaser_certification: usOffering?.purchaserCertification ?? false,
        accreditation_category: usOffering?.accreditationCategory ?? null,
        affirms_accreditation: usOffering?.affirmsAccreditation ?? false,
        acquiring_for_own_account: usOffering?.acquiringForOwnAccount ?? false,
      },
    );
    return res.data;
  }

  /** Null when the contract is not for a US series. */
  async getUsOffering(caseId: string): Promise<UsOfferingInfo | null> {
    const res = await this.api.get<UsOfferingInfo | null>(
      `/api/v1/contracts/cases/${caseId}/us-offering`,
    );
    return res.data;
  }

  async requestAssistance(caseId: string, body: AssistanceRequestBody): Promise<void> {
    await this.api.post(`/api/v1/contracts/cases/${caseId}/request-assistance`, body);
  }

  async listSignedDocuments(caseId: string): Promise<SignedDocument[]> {
    const res = await this.api.get<{ items: SignedDocument[] }>(
      `/api/v1/contracts/cases/${caseId}/documents`,
    );
    return res.data.items;
  }

  async getSignedDocumentDownloadUrl(
    caseId: string,
    documentId: string,
  ): Promise<{ download_url: string; expires_in_seconds: number }> {
    const res = await this.api.get<{ download_url: string; expires_in_seconds: number }>(
      `/api/v1/contracts/cases/${caseId}/documents/${documentId}/download`,
    );
    return res.data;
  }

  async getTimeline(caseId: string): Promise<TimelineEvent[]> {
    const res = await this.api.get<TimelineEvent[]>(
      `/api/v1/workflow/cases/${caseId}/timeline`,
    );
    return res.data;
  }

  /**
   * Upload a file directly to a presigned PUT URL. Bypasses the auth
   * interceptor (presigned URLs carry their own signature in the query
   * string — adding a Bearer token would break the signature).
   *
   * The Content-Type MUST match the value used when generating the URL
   * server-side (application/pdf for signed contracts), otherwise the
   * S3 / MinIO / GCS signature check rejects the PUT.
   */
  async uploadFileToPresignedUrl(
    presignedUrl: string,
    file: File,
    options?: {
      onProgress?: (progress: UploadProgress) => void;
      /** Pass `controller.signal` here; calling `controller.abort()`
       *  triggers a `CanceledError` from axios so the caller can render
       *  "Upload cancelled" without a generic network error. */
      signal?: AbortSignal;
    },
  ): Promise<void> {
    const { onProgress, signal } = options ?? {};
    await axios.put(presignedUrl, file, {
      headers: { 'Content-Type': file.type || 'application/pdf' },
      // No auth, no transformation. Send raw bytes.
      transformRequest: [(data) => data],
      signal,
      onUploadProgress: onProgress
        ? (evt) => {
            const total = evt.total ?? file.size;
            const loaded = evt.loaded;
            const fraction = total > 0 ? Math.min(1, loaded / total) : 0;
            onProgress({ fraction, loaded, total });
          }
        : undefined,
    });
  }
}

export default new ContractsService();
